import { useFlag } from '@reflag/react-sdk';
import { useQuery, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { memo } from 'react';
import { atom, useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import type {
    NavigationScreenSiteState,
    NavigationScreenState,
    NavigationScreenTriageState,
} from '@gitbook/app-navigation';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Sidebar,
    SidebarBody,
    SidebarButton,
    SidebarDisclosureMenu,
    SidebarFooter,
    SidebarHeader,
    SidebarHeading,
    SidebarItemIcon,
    SidebarItemList,
    SidebarItemSkeleton,
    SidebarLink,
    type SidebarLinkProps,
    SidebarToggleButton,
    Status,
    TextInput,
    Tooltip,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    APIErrorBoundary,
    getGitSyncStatusPresentation,
    getSiteCustomizationTabSiteSpaceId,
    getSiteGitSyncSetupTarget,
    hasGitSyncInstallation,
    isFailedGitSyncStatus,
    NewFeatureBadge,
    NotificationsBell,
    OrganizationSearchButton,
    siteCustomizationTabs,
    useGitSyncCurrentStatus,
} from '../../Components';
import { CreateRootSiteItemButton } from '../../Components/Site/SiteStructure/CreateRootSiteItemButton';
import { useNavigation } from '../../navigation';
import { useVisibleSiteSettingsSections } from '../../Screens/Site/components/Settings';
import {
    apiQuery,
    desktopSidebarCollapsedAtom,
    getSitePermissions,
    type SiteCapability,
    type UseUpgradeSite,
    useIsSidebarLocked,
    useUnlockSidebar,
    useUpgradeSite,
} from '../../state';
import { createDocumentationURL, persistRecoilEffect } from '../../utils';
import { SidebarActionsButton } from '../SidebarActionsButton';
import { isOrgLevelScreen, shouldShowOrgContentSidebar } from './ContentSidebar.utils';
import { ContentSidebarContentWithOrgLegacy } from './ContentSidebarLegacy';
import { CustomerIOAlert } from './CustomerIOAlert';
import { FeedbackSidebarButton } from './FeedbackSidebarButton';
import { HelpButton } from './HelpButton';
import { OrganizationAlert } from './OrganizationAlert';
import { OrganizationButton } from './OrganizationButton';
import { SiteCentricNavConfirmDialog } from './SiteCentricNavConfirmDialog';
import { preloadSiteCentricNavConfirmDialogImage } from './SiteCentricNavConfirmDialogAssets';
import { SiteCentricWelcomeDialog } from './SiteCentricWelcomeDialog';
import { SiteSwitcherButton } from './SiteSwitcherButton';
import { SidebarSiteTree, useCollapseAllSiteItems } from './Sites';
import { SpacesTree } from './Spaces';
export interface ContentSidebarProps {
    screen: NavigationScreenState;
}

/**
 * Content sidebar.
 *
 * Renders  the site-centric or legacy content sidebar based on the `SITE_CENTRIC` feature flag.
 */
export function ContentSidebar(props: ContentSidebarProps) {
    return (
        <Sidebar>
            <ContentSidebarContent {...props} />
        </Sidebar>
    );
}

function ContentSidebarContent(props: ContentSidebarProps) {
    const { screen } = props;

    const siteCentricNav = useFlag('SITE_CENTRIC');
    const isSiteCentricNavEnabled = !siteCentricNav.isLoading && siteCentricNav.isEnabled;

    if (!('organization' in screen) || !screen.organization) {
        return <NoOrganizationEmptyState />;
    }

    return (
        <APIErrorBoundary
            fallback={{
                404: <NoOrganizationEmptyState />,
                403: <NoOrganizationEmptyState />,
            }}
        >
            {isSiteCentricNavEnabled ? (
                <ContentSidebarContentWithOrg
                    screen={screen}
                    organizationId={screen.organization}
                />
            ) : (
                <ContentSidebarContentWithOrgLegacy
                    screen={screen}
                    organizationId={screen.organization}
                />
            )}
        </APIErrorBoundary>
    );
}

function NoOrganizationEmptyState() {
    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Organization />
            </EmptyStateIcon>
            <Description>
                Join an organization or create a new one to get started with your content.
            </Description>
            <EmptyStateActions>
                <Button
                    kind="secondary"
                    label="Create organization"
                    href={{ screen: { type: 'welcome' }, overlay: null }}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Memoized content of the sidebar, optimized to re-render only when relevant url parts change.
 */
function ContentSidebarContentWithOrg(props: {
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen } = props;
    const siteId = 'site' in screen ? screen.site : undefined;

    const [{ data: organization }, { data: orgAllContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getOrganizationAllContent', { organizationId }),
        ],
    });
    const siteContent = siteId
        ? orgAllContent.sites.find((item) => item.site.id === siteId)
        : undefined;
    const site = siteContent?.site ?? null;
    const structure = siteContent?.structure ?? null;

    return (
        <>
            <ContentSidebarHeader
                organization={organization}
                organizationContent={orgAllContent}
                screen={screen}
                site={site}
                structure={structure}
            />

            <SidebarBody
                fullWidth
                aria-label="Main navigation"
                className="min-h-0 pb-10 [scrollbar-gutter:stable]"
            >
                {shouldShowOrgContentSidebar(screen, orgAllContent) ? (
                    <OrgContentSidebar
                        content={orgAllContent}
                        organization={organization}
                        screen={screen}
                    />
                ) : (
                    <SiteContentSidebar
                        site={site}
                        structure={structure}
                        organization={organization}
                        orgAllContent={orgAllContent}
                        screen={screen}
                    />
                )}
            </SidebarBody>

            <ContentSidebarFooter organization={organization} screen={screen} />
            <SiteCentricWelcomeDialog />
        </>
    );
}

function OrgContentSidebar(props: {
    content: api.OrganizationAllContent;
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const {
        content,
        screen,
        organization: { id: organizationId, permissions },
    } = props;
    const [isContentSectionExpanded, setIsContentSectionExpanded] = useRecoilState(
        toggleableSidebarSectionExpandedAtoms.content
    );
    const isStyleguideEnabled = useFlag('STYLEGUIDE').isEnabled;
    const shouldShowSpaces = screen.type === 'space' && !screen.site;
    // A styleguide opened at the org level (no site context) shows the list of the
    // organization's styleguides instead of the regular spaces tree.
    const isStyleguideSpace =
        isStyleguideEnabled &&
        screen.type === 'space' &&
        !screen.site &&
        content.sites.some((item) => item.site.styleguide?.space === screen.space);

    return (
        <div className="flex flex-col gap-3">
            <SidebarItemList fullWidth>
                <HomeLink screen={screen} />
                <SitesLink label="Docs sites" screen={screen} />
                <ContentScreenLink screen={screen} />
                <StyleguidesLink screen={screen} />
            </SidebarItemList>

            {permissions.admin ? (
                <SidebarItemList fullWidth>
                    <SidebarHeading label="Admin" />
                    <MembersLink screen={screen} organizationId={organizationId} />
                    <BillingLink screen={screen} organizationId={organizationId} />
                </SidebarItemList>
            ) : null}

            {shouldShowSpaces ? (
                <SidebarDisclosureMenu
                    kind="section"
                    label={isStyleguideSpace ? 'Styleguides' : 'Content'}
                    isExpanded={isContentSectionExpanded}
                    onExpandedChange={setIsContentSectionExpanded}
                    toggleVisibility="active"
                    className="pr-1.5"
                >
                    {isStyleguideSpace ? (
                        <React.Suspense fallback={<SidebarItemSkeleton />}>
                            <StyleguidesContentList
                                organizationId={organizationId}
                                screen={screen}
                            />
                        </React.Suspense>
                    ) : (
                        <SpacesTree
                            organization={props.organization}
                            content={content}
                            screen={screen}
                        />
                    )}
                </SidebarDisclosureMenu>
            ) : null}
        </div>
    );
}

function SiteContentSidebar(props: {
    site: api.Site | null;
    structure: api.SiteStructure | null;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { site, structure, ...rest } = props;

    if (!site) {
        // This state can happen when a screen triggered a redirection
        // so we show a loading state in the sidebar while we fetch the data.
        return <ContentSidebarSkeleton />;
    }

    if (!structure || structure.structure.length === 0) {
        return null;
    }

    return <SiteContentSidebarWithStructure site={site} structure={structure} {...rest} />;
}

function SiteContentSidebarWithStructure(props: {
    site: api.Site;
    structure: api.SiteStructure;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
}) {
    const { organization, orgAllContent, screen, site, structure } = props;
    const { isEnabled: isContextChangeRequestEnabled } = useFlag('SITE_CONTEXT_CHANGE_REQUEST');
    // Site-level Git Sync sits on top of the base native flow: it needs both NATIVE_GIT_SYNC
    // and the NATIVE_GIT_SYNC_SITES add-on. Read both flags unconditionally (never short-circuit
    // a useFlag call) to keep hook order stable across renders.
    const isNativeGitSyncEnabled = useFlag('NATIVE_GIT_SYNC').isEnabled;
    const isNativeGitSyncSitesFlagEnabled = useFlag('NATIVE_GIT_SYNC_SITES').isEnabled;
    const isNativeGitSyncSitesEnabled = isNativeGitSyncEnabled && isNativeGitSyncSitesFlagEnabled;

    const sitePermissions = React.useMemo(
        () => getSitePermissions({ siteStructure: structure, organization }),
        [structure, organization]
    );
    const visibleSettingsSections = useVisibleSiteSettingsSections(site, organization);
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesSections);
    const [isGeneralSectionExpanded, setIsGeneralSectionExpanded] = useRecoilState(
        toggleableSidebarSectionExpandedAtoms.general
    );
    const [isToolsSectionExpanded, setIsToolsSectionExpanded] = useRecoilState(
        toggleableSidebarSectionExpandedAtoms.tools
    );

    const currentSiteView = screen.type === 'site' ? screen.tab : undefined;
    const getSiteScreenHref = (
        tab: SiteView,
        sideSheet?: NavigationScreenSiteState['sideSheet']
    ): SidebarLinkProps['href'] => ({
        screen: {
            type: 'site',
            organization: organization.id,
            site: site.id,
            tab,
            sideSheet,
        },
    });

    const { canViewSiteTools, expandedMenu, showExtendMenu, showSettingsMenu, toggleExpandedMenu } =
        useSiteToolsExpandableMenus({
            currentSiteView,
            screen,
            sitePermissions,
            visibleSettingsSections,
        });

    return (
        <nav aria-label="Site navigation" className="flex w-full flex-none flex-col gap-4">
            <SidebarDisclosureMenu
                kind="section"
                label="General"
                isExpanded={isGeneralSectionExpanded}
                onExpandedChange={setIsGeneralSectionExpanded}
                toggleVisibility="active"
                className="pr-1.5"
            >
                <OverviewLink screen={screen} />
                <PreviewLink screen={screen} />
                <ChangeRequestsLink screen={screen} />
                {isContextChangeRequestEnabled && (
                    <SiteTriageLink
                        organizationId={organization.id}
                        siteId={site.id}
                        screen={screen}
                    />
                )}
                {sitePermissions.canAdminSite ? <StructureLink screen={screen} /> : null}
                {sitePermissions.canAdminSite ? <SiteMembersLink screen={screen} /> : null}
                {/* Up for discussion: <NotificationsBell label="Inbox" icon={Icon.Files} showCount /> */}
            </SidebarDisclosureMenu>

            {/* Tools */}
            {canViewSiteTools ? (
                <SidebarDisclosureMenu
                    kind="section"
                    label="Tools"
                    isExpanded={isToolsSectionExpanded}
                    onExpandedChange={setIsToolsSectionExpanded}
                    toggleVisibility="active"
                    className="pr-1.5"
                >
                    <SiteStyleguideLink
                        site={site}
                        organizationId={organization.id}
                        screen={screen}
                        canAdminSite={sitePermissions.canAdminSite}
                    />

                    {sitePermissions.canAdminSite && isNativeGitSyncSitesEnabled ? (
                        <GitSyncSidebarLink
                            site={site}
                            organizationId={organization.id}
                            screen={screen}
                        />
                    ) : null}

                    {sitePermissions.canAdminSite ? (
                        <SidebarDisclosureMenu
                            label="Customize"
                            icon={Icon.Customization}
                            isExpanded={expandedMenu === 'customize'}
                            onExpandedChange={() => {
                                toggleExpandedMenu('customize');
                            }}
                        >
                            {siteCustomizationTabs.map((tab) => (
                                <SidebarLink
                                    key={tab.id}
                                    label={tab.label}
                                    icon={tab.icon}
                                    isActive={
                                        currentSiteView?.type === 'customization' &&
                                        currentSiteView.tab === tab.id
                                    }
                                    href={getSiteScreenHref({
                                        type: 'customization',
                                        tab: tab.id,
                                        siteSpaceId: getSiteCustomizationTabSiteSpaceId(
                                            tab.id,
                                            currentSiteView?.type === 'customization'
                                                ? currentSiteView.siteSpaceId
                                                : undefined
                                        ),
                                    })}
                                />
                            ))}
                        </SidebarDisclosureMenu>
                    ) : null}

                    <SidebarDisclosureMenu
                        label="Analyze"
                        icon={Icon.Insights}
                        isExpanded={expandedMenu === 'analyze'}
                        onExpandedChange={() => {
                            toggleExpandedMenu('analyze');
                        }}
                    >
                        <SidebarLink
                            label="AI Insights"
                            icon={Icon.Insights}
                            isActive={currentSiteView?.type === 'insights'}
                            href={getSiteScreenHref({
                                type: 'insights',
                                view: { type: 'home' },
                            })}
                        />
                        <SidebarLink
                            label="Analytics"
                            icon={Icon.MaximumValue}
                            isActive={currentSiteView?.type === 'analytics'}
                            href={getSiteScreenHref({
                                type: 'analytics',
                                report: 'traffic',
                            })}
                        />
                    </SidebarDisclosureMenu>

                    {showExtendMenu ? (
                        <SidebarDisclosureMenu
                            label="Extend"
                            icon={Icon.Extend}
                            isExpanded={expandedMenu === 'extend'}
                            onExpandedChange={() => {
                                toggleExpandedMenu('extend');
                            }}
                        >
                            {visibleSettingsSections.connections ? (
                                <SidebarLink
                                    label="Connections"
                                    icon={Icon.Connectors}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'connections'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'connections',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.channels ? (
                                <SidebarLink
                                    label="Channels"
                                    icon={Icon.Channel}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'channels'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'channels',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.embed ? (
                                <SidebarLink
                                    label="Docs Embed"
                                    icon={Icon.DocsEmbed}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'embed'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'embed',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.mcp ? (
                                <SidebarLink
                                    label="MCP access"
                                    icon={Icon.MCP}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'mcp'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'mcp',
                                    })}
                                />
                            ) : null}
                            <SidebarLink
                                label="Integrations"
                                icon={Icon.Integrations}
                                isActive={
                                    screen.type === 'site' &&
                                    screen.sideSheet?.type === 'integrations'
                                }
                                href={getSiteScreenHref(currentSiteView, {
                                    type: 'integrations',
                                })}
                            />
                        </SidebarDisclosureMenu>
                    ) : null}

                    {showSettingsMenu ? (
                        <SidebarDisclosureMenu
                            label="Settings"
                            icon={Icon.Settings}
                            isExpanded={expandedMenu === 'settings'}
                            onExpandedChange={() => {
                                toggleExpandedMenu('settings');
                            }}
                        >
                            {visibleSettingsSections.general ? (
                                <SidebarLink
                                    label="General"
                                    icon={Icon.Options02}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'general'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'general',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.styleguide ? (
                                <SidebarLink
                                    label="Styleguide"
                                    icon={Icon.BookOpen}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'styleguide'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'styleguide',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.agent ? (
                                <SidebarLink
                                    label="Agents"
                                    icon={Icon.AIAgent}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'agent'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'agent',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.audience ? (
                                <SidebarLink
                                    label="Audience"
                                    icon={Icon.PublishStatusPublic}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        (currentSiteView.section === 'audience' ||
                                            currentSiteView.section === 'schema')
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'audience',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.domains ? (
                                <SidebarLink
                                    label="Domain and URL"
                                    icon={Icon.Globe}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'domains'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'domains',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.redirects ? (
                                <SidebarLink
                                    label="Redirects"
                                    icon={Icon.Redirect}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'redirects'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'redirects',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.sponsorship ? (
                                <SidebarLink
                                    label="Ads and sponsorship"
                                    icon={Icon.PaidMember}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'sponsorship'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'sponsorship',
                                    })}
                                />
                            ) : null}
                            {visibleSettingsSections.plan ? (
                                <SidebarLink
                                    label="Plan"
                                    icon={Icon.Upgrade}
                                    isActive={
                                        currentSiteView?.type === 'settings' &&
                                        currentSiteView.section === 'plan'
                                    }
                                    href={getSiteScreenHref({
                                        type: 'settings',
                                        section: 'plan',
                                    })}
                                />
                            ) : null}
                        </SidebarDisclosureMenu>
                    ) : null}
                </SidebarDisclosureMenu>
            ) : null}

            <SiteContentSidebarSiteStructure
                organization={organization}
                orgAllContent={orgAllContent}
                screen={screen}
                site={site}
                sitePermissions={sitePermissions}
                structure={structure}
                upgrade={upgrade}
            />
        </nav>
    );
}

const toggleableSidebarSectionExpandedAtoms = {
    content: atom<boolean>({
        key: 'orgSidebarContentSectionExpanded',
        default: true,
        effects: [persistRecoilEffect()],
    }),
    general: atom<boolean>({
        key: 'siteSidebarGeneralSectionExpanded',
        default: true,
        effects: [persistRecoilEffect()],
    }),
    tools: atom<boolean>({
        key: 'siteSidebarToolsSectionExpanded',
        default: true,
        effects: [persistRecoilEffect()],
    }),
};

type SiteDisclosureMenuId = 'customize' | 'analyze' | 'extend' | 'settings';
type SiteView = NavigationScreenSiteState['tab'];

function useSiteToolsExpandableMenus(props: {
    currentSiteView: SiteView | undefined;
    screen: NavigationScreenState;
    sitePermissions: SiteCapability;
    visibleSettingsSections: ReturnType<typeof useVisibleSiteSettingsSections>;
}) {
    const { currentSiteView, screen, sitePermissions, visibleSettingsSections } = props;

    const canViewSiteTools = sitePermissions.canViewSite || sitePermissions.canAdminSite;
    const showCustomizeMenu = sitePermissions.canAdminSite;
    const showExtendMenu = sitePermissions.canAdminSite;
    const showSettingsMenu =
        sitePermissions.canAdminSite &&
        (visibleSettingsSections.general ||
            visibleSettingsSections.agent ||
            visibleSettingsSections.audience ||
            visibleSettingsSections.domains ||
            visibleSettingsSections.redirects ||
            visibleSettingsSections.sponsorship ||
            visibleSettingsSections.plan);

    const isCustomizeActive = currentSiteView?.type === 'customization';
    const isAnalyzeActive =
        currentSiteView?.type === 'analytics' || currentSiteView?.type === 'insights';
    const isExtendActive =
        currentSiteView?.type === 'settings' &&
        ((visibleSettingsSections.mcp && currentSiteView.section === 'mcp') ||
            (visibleSettingsSections.connections && currentSiteView.section === 'connections') ||
            (visibleSettingsSections.channels && currentSiteView.section === 'channels') ||
            (visibleSettingsSections.embed && currentSiteView.section === 'embed'));
    const isIntegrationsActive =
        screen.type === 'site' && screen.sideSheet?.type === 'integrations';
    const isSettingsActive =
        currentSiteView?.type === 'settings' &&
        ((visibleSettingsSections.general && currentSiteView.section === 'general') ||
            (visibleSettingsSections.agent && currentSiteView.section === 'agent') ||
            (visibleSettingsSections.audience &&
                (currentSiteView.section === 'audience' || currentSiteView.section === 'schema')) ||
            (visibleSettingsSections.domains && currentSiteView.section === 'domains') ||
            (visibleSettingsSections.redirects && currentSiteView.section === 'redirects') ||
            (visibleSettingsSections.sponsorship && currentSiteView.section === 'sponsorship') ||
            (visibleSettingsSections.plan && currentSiteView.section === 'plan'));

    const activeMenu: SiteDisclosureMenuId | null = canViewSiteTools
        ? showCustomizeMenu && isCustomizeActive
            ? 'customize'
            : isAnalyzeActive
              ? 'analyze'
              : showExtendMenu && (isExtendActive || isIntegrationsActive)
                ? 'extend'
                : showSettingsMenu && isSettingsActive
                  ? 'settings'
                  : null
        : null;

    const [expandedMenu, setExpandedMenu] = React.useState<SiteDisclosureMenuId | null>(activeMenu);
    const toggleExpandedMenu = useEventCallback((menu: SiteDisclosureMenuId) => {
        setExpandedMenu((current) => (current === menu ? null : menu));
    });

    React.useEffect(() => {
        if (activeMenu) {
            setExpandedMenu(activeMenu);
        }
    }, [activeMenu]);

    return {
        canViewSiteTools,
        expandedMenu,
        showCustomizeMenu,
        showExtendMenu,
        showSettingsMenu,
        toggleExpandedMenu,
    };
}

function SiteContentSidebarSiteStructure(props: {
    site: api.Site;
    structure: api.SiteStructure;
    organization: api.Organization;
    orgAllContent: api.OrganizationAllContent;
    screen: NavigationScreenState;
    sitePermissions: SiteCapability;
    upgrade: UseUpgradeSite | null;
}) {
    const { organization, orgAllContent, screen, site, sitePermissions, structure, upgrade } =
        props;

    const { hasExpandedItems, collapseAllItems } = useCollapseAllSiteItems();

    const [showSearch, setShowSearch] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const isSearchActive = showSearch && query.trim().length > 0;

    return (
        <div className="flex min-h-52 flex-none flex-col gap-px">
            <div className="flex items-center justify-between px-3 pr-1.5">
                <SidebarHeading label="Content" />

                <div className="pointer-events-none flex items-center gap-1 opacity-0 transition-opacity group-hover/sidebar:pointer-events-auto group-hover/sidebar:opacity-100 has-[[aria-expanded=true]]:pointer-events-auto has-[[aria-expanded=true]]:opacity-100 group-has-[:focus-visible]/sidebar:pointer-events-auto group-has-[[aria-haspopup=true][aria-expanded=true]]/sidebar:pointer-events-auto group-has-[:focus-visible]/sidebar:opacity-100 group-has-[[aria-haspopup=true][aria-expanded=true]]/sidebar:opacity-100">
                    {!isSearchActive ? (
                        <Button
                            label="Collapse all"
                            leadingIcon={Icon.CollapseAll}
                            iconOnly
                            kind="blank"
                            size="small"
                            disabled={!hasExpandedItems}
                            aria-hidden={!hasExpandedItems}
                            onPress={collapseAllItems}
                            className={clsx(
                                hasExpandedItems
                                    ? 'opacity-1'
                                    : 'pointer-events-none opacity-0 transition-opacity'
                            )}
                        />
                    ) : null}

                    <Button
                        label={showSearch ? 'Hide site search' : 'Show site search'}
                        leadingIcon={Icon.Search}
                        iconOnly
                        kind="blank"
                        size="small"
                        onPress={() => {
                            if (showSearch) {
                                setQuery('');
                            }
                            setShowSearch((prev) => !prev);
                        }}
                    />

                    {sitePermissions.canAdminSite ? (
                        <>
                            <Button
                                label="Manage site structure"
                                leadingIcon={Icon.Edit}
                                iconOnly
                                kind="blank"
                                size="small"
                                href={{
                                    screen: {
                                        type: 'site',
                                        organization: organization.id,
                                        site: site.id,
                                        tab: { type: 'structure' },
                                    },
                                }}
                            />

                            <CreateRootSiteItemButton
                                label="Add content"
                                organization={organization}
                                site={site}
                                siteStructure={structure}
                                orgAllContent={orgAllContent}
                                upgrade={upgrade}
                                iconOnly
                                size="small"
                                kind="blank"
                            />
                        </>
                    ) : null}
                </div>
            </div>

            <div className="w-full overflow-x-hidden overflow-y-visible pr-1.5 pl-3">
                <div
                    aria-hidden={!showSearch}
                    className="grid transition-[grid-template-rows] duration-200 ease-out"
                    style={{ gridTemplateRows: showSearch ? '1fr' : '0fr' }}
                >
                    <div className="overflow-hidden px-1 pt-1">
                        <TextInput
                            autoFocus={showSearch}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onClear={() => setQuery('')}
                            leadingIcon={Icon.Search}
                            placeholder="Search site content"
                            className="mb-2 w-full"
                            disabled={!showSearch}
                        />
                    </div>
                </div>

                <SidebarSiteTree
                    siteContent={{ site, structure }}
                    organization={organization}
                    organizationId={organization.id}
                    orgAllContent={orgAllContent}
                    screen={screen}
                    sitePermissions={sitePermissions}
                    upgrade={upgrade}
                    searchQuery={showSearch ? query : ''}
                />
            </div>
        </div>
    );
}

const ContentSidebarHeader = memo(function ContentSidebarHeader(props: {
    site: api.Site | null;
    screen: NavigationScreenState;
    organization: api.Organization;
    structure: api.SiteStructure | null | undefined;
    organizationContent: api.OrganizationAllContent;
}) {
    const { organization, organizationContent, screen, site, structure } = props;

    const [isCollapsed, setCollapse] = useRecoilState(desktopSidebarCollapsedAtom);
    const isLocked = useIsSidebarLocked();
    const unlock = useUnlockSidebar();

    const siteId = 'site' in screen && screen.site;

    return (
        <SidebarHeader>
            <div className="flex min-w-0 flex-1 flex-col space-y-3">
                <div className="flex w-full min-w-0 items-center">
                    <div className="flex flex-1">
                        <OrganizationButton
                            organization={organization}
                            screen={screen}
                            iconOnly={!isOrgLevelScreen(screen)}
                        />
                    </div>

                    <div className="flex items-center gap-0.5">
                        <OrganizationSearchButton
                            data-testid="sidebar.findButton"
                            organization={organization}
                        />
                        <NotificationsBell iconOnly placement="bottom-start" />

                        {/* We only show the toggle button when the sidebar is closed. 
                Collapsing it handled only by the sidebar resize bar. */}
                        {isCollapsed && (
                            <SidebarToggleButton
                                isCollapsed={isLocked || isCollapsed}
                                onCollapse={() => {
                                    setCollapse(true);
                                }}
                                onUncollapse={() => {
                                    unlock();
                                    setCollapse(false);
                                }}
                                outlined={false}
                            />
                        )}
                    </div>
                </div>

                {siteId && site ? (
                    <SiteSwitcherButton
                        organization={organization}
                        organizationId={organization.id}
                        canCreateSite={organization.permissions.createContent}
                        organizationContent={organizationContent}
                        site={site}
                        structure={structure ?? null}
                    />
                ) : null}
            </div>
        </SidebarHeader>
    );
});

export const ContentSidebarFooter = memo(function ContentSidebarFooter(props: {
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const { organization, screen } = props;
    const { navigate } = useNavigation();

    const siteCentricNav = useFlag('SITE_CENTRIC');
    const isSiteCentricNavEnabled = !siteCentricNav.isLoading && siteCentricNav.isEnabled;
    const canToggleSiteCentricNav = useFlag('SITE_CENTRIC_TOGGLE').isEnabled;

    return (
        <>
            <SidebarFooter className="mb-2 pb-8 [&:has(>#customerio-alert:only-child:empty)]:hidden">
                <OrganizationAlert data-org-alerts organization={organization} />
                <CustomerIOAlert
                    onBannerAppears={
                        canToggleSiteCentricNav
                            ? preloadSiteCentricNavConfirmDialogImage
                            : undefined
                    }
                />
                {canToggleSiteCentricNav ? (
                    <SiteCentricNavConfirmDialog
                        organizationId={organization.id}
                        isSiteCentricNavEnabled={isSiteCentricNavEnabled}
                    />
                ) : null}
            </SidebarFooter>

            <div className="absolute inset-x-2 bottom-2 flex items-center gap-1 bg-gradient-to-t from-30% from-[--sp-color-bg-side] to-transparent">
                <DocsButton />
                <HelpButton />
                <FeedbackSidebarButton label="Share feedback" outlined iconOnly />
                {!isSiteCentricNavEnabled ? (
                    <SidebarButton
                        iconOnly
                        outlined
                        label="Settings"
                        icon={Icon.Settings}
                        onPress={() => {
                            navigate({
                                screen: {
                                    type: 'org_settings',
                                    organization: organization.id,
                                    tab: { type: 'profile' },
                                },
                            });
                        }}
                    />
                ) : null}
                {isSiteCentricNavEnabled ? (
                    <AgentsLink screen={screen} iconOnly outlined rounded />
                ) : null}
            </div>
        </>
    );
});

function DocsButton() {
    return (
        <PaletteButton
            label="Docs"
            leadingIcon={Icon.Documentation}
            iconOnly
            kind="secondary"
            rounded
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'docs',
                            commands: [
                                {
                                    key: 'docs.documentation',
                                    title: 'Documentation',
                                    icon: Icon.BookOpen,
                                    trigger: () => {
                                        window.open(createDocumentationURL(''), '_blank');
                                    },
                                },
                                {
                                    key: 'docs.guides',
                                    title: 'Guides',
                                    icon: Icon.Documentation,
                                    trigger: () => {
                                        window.open(createDocumentationURL('guides'), '_blank');
                                    },
                                },
                            ],
                        },
                    ],
                })
            }
        />
    );
}

/**
 * Home sidebar links.
 */
export function HomeLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;

    return (
        <SidebarLink
            label="Home"
            icon={Icon.Home}
            isActive={screen.type === 'home'}
            href={{
                screen: {
                    type: 'home',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

export function SitesLink(props: { screen: NavigationScreenState; label?: string }) {
    const { screen, label = 'Docs sites' } = props;

    return (
        <SidebarLink
            label={label}
            icon={Icon.DocsSite}
            isActive={screen.type === 'sites'}
            href={{
                screen: {
                    type: 'sites',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function ContentScreenLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <Tooltip label="Find all your spaces here" position="right">
            <SidebarLink
                label="Content"
                icon={Icon.AllSpaces}
                isActive={screen.type === 'org_content'}
                href={{
                    screen: {
                        type: 'org_content',
                        organization: 'organization' in screen ? screen.organization : undefined,
                    },
                }}
            />
        </Tooltip>
    );
}

function StyleguidesLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    const isStyleguideEnabled = useFlag('STYLEGUIDE').isEnabled;

    if (!isStyleguideEnabled) {
        return null;
    }

    return (
        <SidebarLink
            label="Styleguides"
            icon={Icon.BookOpen}
            isActive={screen.type === 'styleguides'}
            href={{
                screen: {
                    type: 'styleguides',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

/**
 * Sidebar list of the organization's styleguides, shown in place of the spaces
 * tree when a styleguide is opened at the org level.
 */
function StyleguidesContentList(props: { organizationId: string; screen: NavigationScreenState }) {
    const { organizationId, screen } = props;
    const { data } = useSuspenseQuery(apiQuery('listOrganizationStyleguides', { organizationId }));
    const currentSpace = screen.type === 'space' ? screen.space : undefined;

    return (
        <>
            {data.styleguides.map((styleguide) => (
                <SidebarLink
                    key={styleguide.space.id}
                    label={styleguide.space.title}
                    icon={Icon.BookOpen}
                    isActive={currentSpace === styleguide.space.id}
                    href={{
                        screen: {
                            type: 'space',
                            organization: organizationId,
                            space: styleguide.space.id,
                            page: '',
                        },
                    }}
                />
            ))}
        </>
    );
}

/**
 * Site-level styleguide link shown at the top of the Tools section. Points to
 * the styleguide space when the site already has one, otherwise to the styleguide
 * settings section where a styleguide can be created or picked (admins only).
 */
function SiteStyleguideLink(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
    canAdminSite: boolean;
}) {
    const { site, organizationId, screen, canAdminSite } = props;
    const isStyleguideEnabled = useFlag('STYLEGUIDE').isEnabled;
    const { navigate } = useNavigation();

    if (!isStyleguideEnabled) {
        return null;
    }

    const styleguideSpace = site.styleguide?.space;

    // Without a styleguide yet, only admins can create one — hide the link otherwise.
    if (!styleguideSpace && !canAdminSite) {
        return null;
    }

    // Without a styleguide yet, link to the styleguide settings section (where one
    // can be created or picked) and surface a "Set up" affordance, mirroring Git Sync.
    if (!styleguideSpace) {
        return (
            <SidebarLink
                label="Styleguide"
                icon={Icon.BookOpen}
                isActive={
                    screen.type === 'site' &&
                    screen.tab?.type === 'settings' &&
                    screen.tab.section === 'styleguide'
                }
                href={{
                    screen: {
                        type: 'site',
                        organization: organizationId,
                        site: site.id,
                        tab: { type: 'settings', section: 'styleguide' },
                    },
                }}
            >
                <span className="ml-auto text-muted">
                    Set up <SidebarItemIcon icon={Icon.ChevronRight} />
                </span>
            </SidebarLink>
        );
    }

    return (
        // The actions button is rendered as an absolute sibling on top of the link
        // rather than inside it: a react-aria pressable nested in the link's anchor
        // would bubble its press to the link and trigger navigation.
        <div className="group/sidebar-item relative">
            <SidebarLink
                label="Styleguide"
                icon={Icon.BookOpen}
                isActive={screen.type === 'space' && screen.space === styleguideSpace}
                href={{
                    screen: {
                        // Open the styleguide in the context of the site it's accessed
                        // from, so the URL stays site-scoped
                        // (/o/{org}/sites/{site}/s/{space}/).
                        type: 'space',
                        space: styleguideSpace,
                        organization: organizationId,
                        site: site.id,
                        page: '',
                    },
                }}
            />
            {canAdminSite ? (
                <div className="absolute inset-y-0 right-1 flex items-center">
                    <SidebarActionsButton
                        palette={() =>
                            createPalette({
                                groups: [
                                    {
                                        key: 'styleguide',
                                        commands: [
                                            {
                                                key: 'manage',
                                                title: 'Manage styleguide',
                                                icon: Icon.Settings,
                                                trigger: () => {
                                                    navigate({
                                                        screen: {
                                                            type: 'site',
                                                            organization: organizationId,
                                                            site: site.id,
                                                            tab: {
                                                                type: 'settings',
                                                                section: 'styleguide',
                                                            },
                                                        },
                                                    });
                                                },
                                            },
                                        ],
                                    },
                                ],
                            })
                        }
                    />
                </div>
            ) : null}
        </div>
    );
}

export function AgentsLink(props: SidebarLinkProps & { screen: NavigationScreenState }) {
    const { screen, iconOnly, rounded, outlined } = props;
    const featureAgentEnabled = useFlag('DOCS_AGENTS').isEnabled;

    if (featureAgentEnabled) {
        return null;
    }

    return (
        <SidebarLink
            label="GitBook Agent"
            icon={Icon.AIAgent}
            iconOnly={iconOnly}
            rounded={rounded}
            outlined={outlined}
            isActive={screen.type === 'agents'}
            href={{
                screen: {
                    type: 'agents',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

/**
 * Site sidebar links.
 */
function OverviewLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Overview"
            icon={Icon.Grid}
            isActive={screen.type === 'site' && !screen.tab && !screen.sideSheet}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: undefined,
                    sideSheet: undefined,
                },
            }}
        />
    );
}

function PreviewLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Preview"
            icon={Icon.SitePreview}
            isActive={screen.type === 'site' && screen.tab?.type === 'preview'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'preview' },
                },
            }}
        />
    );
}

function StructureLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Site structure"
            icon={Icon.SiteStructure}
            isActive={screen.type === 'site' && screen.tab?.type === 'structure'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'structure' },
                },
            }}
        />
    );
}

function SiteMembersLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Members"
            icon={Icon.Users}
            isActive={screen.type === 'site' && screen.tab?.type === 'members'}
            href={{
                screen: {
                    type: 'site',
                    site: 'site' in screen ? screen.site : undefined,
                    tab: { type: 'members' },
                },
            }}
        />
    );
}

export function ChangeRequestsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Change requests"
            icon={Icon.ChangeRequest}
            isActive={screen.type === 'change_requests'}
            href={{
                screen: {
                    type: 'change_requests',
                    organization: 'organization' in screen ? screen.organization : undefined,
                    site: 'site' in screen ? screen.site : undefined,
                },
            }}
        />
    );
}

function GitSyncSidebarLink(props: {
    site: api.Site;
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen, site } = props;
    const hasInstallation = hasGitSyncInstallation(site);
    const installationProvider = site.gitSync?.installationProvider;
    const gitSyncStatus = useGitSyncCurrentStatus(site);
    const isPending =
        site.gitSync?.installationStatus &&
        site.gitSync.installationStatus !== api.GitSyncInstallationStatus.Active;
    const presentation = getGitSyncStatusPresentation({
        gitSyncStatus,
        hasInstallation,
        isPending: Boolean(isPending),
    });

    // A failed sync opens the sync status view (matching the space behaviour) instead of the
    // configuration. This link only renders behind the native site flow, so no extra flag gate is
    // needed here.
    const isSyncFailed = isFailedGitSyncStatus(gitSyncStatus);
    const setupTarget = getSiteGitSyncSetupTarget(
        site,
        organizationId,
        isSyncFailed && site.gitSync?.installationId ? 'sync' : undefined
    );

    const sidebarStatus = gitSyncStatus ? (
        <Status
            kind={presentation.kind}
            label={presentation.label}
            size="small"
            loading={presentation.loading}
            detail={presentation.detail}
            detailTooltip
        />
    ) : presentation.actionLabel ? (
        <span className="text-muted">
            {presentation.actionLabel} <SidebarItemIcon icon={Icon.ChevronRight} />
        </span>
    ) : null;

    return (
        <SidebarLink
            label="Git Sync"
            icon={installationProvider === api.GitSyncProvider.Gitlab ? Icon.GitLab : Icon.GitHub}
            isActive={screen.type === 'site' && screen.sideSheet?.type === 'gitsync-setup'}
            tooltip={{
                position: 'right',
                label: gitSyncStatus ? undefined : presentation.detail,
            }}
            href={setupTarget}
        >
            {sidebarStatus ? <span className="ml-auto">{sidebarStatus}</span> : null}
        </SidebarLink>
    );
}

function MembersLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { organizationId } = props;
    return (
        <SidebarLink
            label="Members"
            icon={Icon.Users}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: organizationId,
                    tab: { type: 'members' },
                },
            }}
        />
    );
}

function BillingLink(props: { screen: NavigationScreenState; organizationId: string }) {
    const { organizationId } = props;
    return (
        <SidebarLink
            label="Billing"
            icon={Icon.Upgrade}
            href={{
                screen: {
                    type: 'org_settings',
                    organization: organizationId,
                    tab: { type: 'plans' },
                },
            }}
        />
    );
}

function SiteTriageLink(props: {
    organizationId: string;
    siteId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, siteId, screen } = props;
    const { data } = useQuery(
        apiQuery('listSiteFindings', {
            organizationId,
            siteId,
            status: api.SiteFindingStatus.Open,
            limit: 1,
        })
    );
    const count = data?.count;

    return (
        <SidebarLink
            label="Triage"
            icon={Icon.Findings}
            isActive={screen.type === 'triage' && screen.site === siteId}
            href={{
                screen: {
                    type: 'triage',
                    organization: organizationId,
                    site: siteId,
                    // Reset to the bare `/triage` findings view, clearing any selected
                    // finding, sub-view, scan, or filters carried over from the current screen.
                    finding: undefined,
                    view: undefined,
                    filters: undefined,
                    scan: undefined,
                    scanFilters: undefined,
                } satisfies NavigationScreenTriageState,
            }}
        >
            {count ? (
                <Badge label={count} kind="secondary" size="xsmall" className="ml-auto" thin />
            ) : count === 0 ? (
                // Highlight Triage as new only while the queue is empty, so an
                // active queue count takes precedence over the "New" badge.
                <span className="ml-auto">
                    <NewFeatureBadge flagKey="SITE_CONTEXT_CHANGE_REQUEST" />
                </span>
            ) : null}
        </SidebarLink>
    );
}

function ContentSidebarSkeleton() {
    return (
        <div className="flex flex-col gap-3">
            {/* <Divider /> */}
            <div className="space-y-2 px-3 pr-1.5">
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
            </div>
            {/* <Divider /> */}
            <div className="space-y-2 px-3 pr-1.5">
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
                <SidebarItemSkeleton />
            </div>
        </div>
    );
}
